import { createAsyncAction, PayloadAC, PayloadAction } from 'typesafe-actions';
import { DataFetchingThunkAction, MiddlewareDependencies } from '../types';
import { getErrorCustomData } from '../../../common/errors/CustomFetchApiError';
import { Dictionary } from '../../../../types/globals';
import { dataFetchActions } from './actions';

export const FETCH_START = 'FETCH_START';
export const FETCH_SUCCESS = 'FETCH_SUCCESS';
export const FETCH_FAILED = 'FETCH_FAILED';

export interface FetchAction {
    identifier: string;
}

export interface DefaultErrorPayload extends FetchAction {
    error: Error | string;
}

export interface SuccessFetchActionPayload<T, ReducerPayloadOptions = Dictionary<any>> {
    identifier: string;
    data: T;
    reducerPayloadOptions?: ReducerPayloadOptions;
}

export const createLocalActionType = (prefix: string) => (type: string) => `${prefix}/${type}`;

export const createFetchActions = <
    SuccessData,
    FetchPayload extends FetchAction = FetchAction,
    FailedPayload extends FetchAction = DefaultErrorPayload
>(
    prefix: string
) =>
    createAsyncAction(
        createLocalActionType(prefix)(FETCH_START),
        createLocalActionType(prefix)(FETCH_SUCCESS),
        createLocalActionType(prefix)(FETCH_FAILED)
    )<FetchPayload, SuccessFetchActionPayload<SuccessData>, FailedPayload>();

export interface SyncDataFetchActions<Data> {
    request: PayloadAC<string, FetchAction>;
    success: PayloadAC<string, SuccessFetchActionPayload<Data>>;
    failure: PayloadAC<string, DefaultErrorPayload>;
}

export interface GeneratedActions<T extends Array<any>, Data> {
    actions: SyncDataFetchActions<Data>;
    thunk: (...args: T) => DataFetchingThunkAction;
}

type FetchDataFunc<T extends Array<any>, U> = (...args: T) => Promise<U>;

export const createDataFetchActions = <FetchArgs extends Array<any>, FetchResponseType>(
    prefix: string,
    getFetchData: (middlewareDependencies: MiddlewareDependencies) => FetchDataFunc<FetchArgs, FetchResponseType>,
    getId: (...args: Parameters<ReturnType<typeof getFetchData>>) => string,
    getReducerPayloadOptions?: (...args: Parameters<ReturnType<typeof getFetchData>>) => Dictionary<any>
    // eslint-disable-next-line max-params
): GeneratedActions<FetchArgs, FetchResponseType> => {
    const actions = createFetchActions<FetchResponseType>(prefix);

    const thunk = (...args: FetchArgs): DataFetchingThunkAction => async (
        dispatch,
        getState,
        middlewareDependencies
    ) => {
        const identifier = getId(...args);
        const { logger } = middlewareDependencies;
        let reducerPayloadOptions;
        if (getReducerPayloadOptions) {
            reducerPayloadOptions = getReducerPayloadOptions(...args);
        }
        const isCurrentRequestMatchId = () => {
            const { dataFetching } = getState();
            const currentRequestId = dataFetching[prefix] ? dataFetching[prefix].requestId : '';
            return identifier === currentRequestId;
        };
        if (isCurrentRequestMatchId()) {
            return;
        }

        dispatch(dataFetchActions.request({ identifier, requestIssuer: prefix }));
        dispatch(actions.request({ identifier }));
        try {
            const data = await getFetchData(middlewareDependencies)(...args);

            if (!isCurrentRequestMatchId()) {
                return;
            }
            dispatch(dataFetchActions.success({ identifier, requestIssuer: prefix }));
            dispatch(actions.success({ identifier, data, reducerPayloadOptions }));
        } catch (err) {
            logger.error(err, {
                action: `${prefix}-fetchDataThunk`,
                ...getErrorCustomData(err),
                payload: { identifier, args },
            });
            if (!isCurrentRequestMatchId()) {
                return;
            }

            dispatch(dataFetchActions.failure({ error: err, requestIssuer: prefix, identifier }));
            dispatch(actions.failure({ error: err, identifier }));
        }
    };

    return {
        actions,
        thunk,
    };
};

export interface DataFetchingState {
    identifier: string;
    isFetching: boolean;
    isFetchingComplete?: boolean;
    didInvalidate: boolean;
    error?: Error | string | null;
    reducerPayloadOptions?: Dictionary<any>;
}

export const defaultRequestHandler = <T extends DataFetchingState>(
    state: T,
    { payload }: PayloadAction<string, FetchAction>,
    defaultState?: T
) => {
    if (state.isFetching && payload.identifier === state.identifier) {
        return state;
    }

    const nextState = defaultState || state;

    return {
        ...nextState,
        isFetching: true,
        identifier: payload.identifier,
        didInvalidate: false,
        error: undefined,
    };
};

export const defaultErrorHandler = <T extends DataFetchingState>(
    state: T,
    { payload }: PayloadAction<string, DefaultErrorPayload>
) => {
    if (state.identifier !== payload.identifier) {
        return state;
    }
    return {
        ...state,
        isFetching: false,
        didInvalidate: true,
        error: payload.error,
    };
};
